// The Inbox's drawer (#808, #795 decision 2): an item answered beside the list, with no
// page load.
//
// **It answers with the controls the item's own screen used**, unchanged: the approval
// card, the consent card, the partner-access request and the invitation card, each with
// its own step-up where it had one (#809 moves the step-up; this does not). So the drawer
// adds no second set of gates to keep in step with the first.
//
// **It acts in the item's own context.** Every answering route reads the session's
// selected context and nothing in the request (`routes/changes.py::_answerer`,
// `routes/consent.py::_workspace`), so an item of another workspace is answered by selecting
// that workspace first — the organisation, for a change its grant holds. The location
// names the item and its context (`inboxView.ts`), and `useConsole` selects it as it does
// for a change link; the drawer waits for the session to be there before it reads or
// offers anything, and says so. An invitation needs no context.
//
// **Each kind's answerable row comes from the read its old screen took**, filtered to the
// one item. Where the row cannot be found under the item's context, the drawer says why,
// in a sentence, and offers a link only where loading it again could help (#858): the
// item was answered (by whom, when the inbox lists it) or expired, and no link; the
// person holds no membership in its context, and no link; the selection failed some other
// way, and "Open in <workspace>", the same location as a page load.
//
// **It says where the person has landed** when its item's link moved the console to
// another context, which the console keeps once the drawer closes (#858).
//
// A modal `<dialog>`: the page behind it is inert, and Tab wraps at its ends rather than
// leaving for the browser's own controls, so focus stays inside; Esc closes it; focus
// returns to the row that opened it; it is labelled by its heading.

import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { severityLabel, utcTime } from "../api/automation";
import type { ChangeRow } from "../api/changes";
import type { ConsentRead } from "../api/consent";
import { investigateLiveIn, type GrantsRead } from "../api/grants";
import { messageOf } from "../api/http";
import type { AnsweredItem, InboxItem, InboxKind } from "../api/inbox";
import type { Membership } from "../api/memberships";
import type { ContextChoice, SessionResponse } from "../api/session";
import { GrantRequest, type OnAnswer as OnAnswerGrant } from "./AccessGrantsPage";
import { ApprovalCard, ApprovedChangeProgress, type OnAnswerChange } from "./ApprovalCard";
import { Notice } from "./chrome";
import { ConsentCard, type OnAnswerConsent } from "./ConsentPage";
import { decisionReceipt } from "./decisionState";
import { answerSentence, rowOf, type RowItem } from "./inboxKinds";
import { INBOX_KINDS, inboxPath, openKey, type OpenItem } from "./inboxView";
import { runPagePath } from "../runId";
import { inLinkContext } from "./routes";
import { useStepUpFactors, type BeginPasskey, type StepUpFactors } from "./StepUp";
import { WaitingInvitations } from "./WaitingInvitations";

/** The console's reads a drawer answers from, as `useConsole` holds them. */
export interface DrawerSources {
  consent: ConsentRead | null;
  consentError: string | null;
  changes: ChangeRow[] | null;
  changesError: string | null;
  access: GrantsRead | null;
  accessError: string | null;
}

/** The answers, as the old screens were handed them. */
export interface DrawerActions {
  /** Start a held alarm's investigation now, past the daily limit, or dismiss it (#849). */
  investigateHeld?: (id: string) => Promise<{ run_id: string | null; joined: boolean }>;
  dismissHeld?: (id: string) => Promise<void>;
  answerConsent: OnAnswerConsent;
  answerChange: OnAnswerChange;
  answerGrant: OnAnswerGrant;
  accept: (inviteId: string) => Promise<void>;
  stepUpFactors: () => Promise<StepUpFactors>;
  beginPasskey?: BeginPasskey;
}

interface BodyProps<K extends InboxKind> {
  id: string;
  /** The inbox's row for the item, when the inbox has been read and still holds it. */
  item: Extract<InboxItem, { kind: K }> | null;
  sources: DrawerSources;
  actions: DrawerActions;
  address: string;
  /** Whether an answer was given here: the item leaving its read is then the receipt's. */
  answered: boolean;
  onAnswered: (receipt: string) => void;
  onClose: () => void;
  /** What to say, and to offer, when the item's row is not where it should be. */
  absent: ReactNode;
}

/** What a read is doing: in flight, refused, or answered with or without the item. */
function Reading<T>({
  value,
  error,
  children,
}: {
  value: T | null;
  error: string | null;
  children: (value: T) => ReactNode;
}) {
  if (error !== null) return <Notice tone="bad">{error}</Notice>;
  if (value === null) return <p role="status">Reading the request…</p>;
  return <>{children(value)}</>;
}

function ChangeBody({ id, sources, actions, answered, onAnswered, absent }: BodyProps<"change_approval">) {
  const factors = useStepUpFactors(actions.stepUpFactors);
  const [outcome, setOutcome] = useState<string | null>(null);
  return (
    <Reading value={sources.changes} error={sources.changesError}>
      {(changes) => {
        const change = changes.find((row) => row.change_id === id);
        if (change === undefined) return outcome === "approved" ? <ApprovedChangeProgress changeId={id} /> : answered ? null : absent;
        return (
          <ApprovalCard
            change={change}
            factors={factors}
            beginPasskey={actions.beginPasskey}
            onAnswer={async (...args) => {
              const result = await actions.answerChange(...args);
              if (result) { setOutcome(result.outcome); onAnswered(decisionReceipt(result.outcome)); }
              return result;
            }}
          />
        );
      }}
    </Reading>
  );
}

function ConsentBody({ id, sources, actions, answered, onAnswered, absent }: BodyProps<"consent_request">) {
  const factors = useStepUpFactors(actions.stepUpFactors);
  return (
    <Reading value={sources.consent} error={sources.consentError}>
      {(consent) => {
        const row = consent.requests.find((request) => request.request_id === id);
        if (row === undefined) return answered ? null : absent;
        return (
          <ConsentCard
            row={row}
            factors={factors}
            beginPasskey={actions.beginPasskey}
            onAnswer={async (...args) => {
              const result = await actions.answerConsent(...args);
              if (result) onAnswered(decisionReceipt(result.outcome));
              return result;
            }}
          />
        );
      }}
    </Reading>
  );
}

function CapabilityBody({ id, sources, actions, answered, onAnswered, absent }: BodyProps<"capability_request">) {
  const factors = useStepUpFactors(actions.stepUpFactors);
  return (
    <Reading value={sources.access} error={sources.accessError}>
      {(access) => {
        const row = access.waiting.find((grant) => grant.id === id);
        if (row === undefined) return answered ? null : absent;
        return (
          <GrantRequest
            row={row}
            investigateLive={investigateLiveIn(access.standing)(row.org_id)}
            factors={factors}
            beginPasskey={actions.beginPasskey}
            onAnswer={async (grantId, decision, stepUp) => {
              await actions.answerGrant(grantId, decision, stepUp);
              onAnswered(
                decision === "granted"
                  ? "Allowed. The partner keeps it until you take it back under Partner access."
                  : "Declined. The partner can ask again.",
              );
            }}
          />
        );
      }}
    </Reading>
  );
}

function InvitationBody({ item, actions, address, onClose, absent }: BodyProps<"invitation">) {
  if (item === null) return <>{absent}</>;
  return (
    <WaitingInvitations
      invitations={[item.detail]}
      address={address}
      error={null}
      onAccept={async (inviteId) => {
        await actions.accept(inviteId);
        onClose();
      }}
      shape="full"
    />
  );
}

/**
 * A held alarm (#849): answered from the inbox's own row, in its workspace. Started now,
 * past the daily limit on automatic investigations (the spend gate still decides), or
 * dismissed, which starts nothing. One whose alert has since resolved says so, and asks
 * before starting its investigation (#871). The question replaces the buttons in place, so
 * focus moves to its answer, and back to the button that asked, rather than to the page.
 */
function HeldAlertBody({ item, actions, answered, onAnswered, absent }: BodyProps<"held_alert">) {
  const [busy, setBusy] = useState<"investigate" | "dismiss" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<boolean | null>(null);
  const yesRef = useRef<HTMLButtonElement>(null);
  const investigateRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming === true) yesRef.current?.focus();
    else if (confirming === false) investigateRef.current?.focus();
  }, [confirming]);
  if (answered) {
    return run === null ? null : (
      <p>
        <a href={runPagePath(run)}>Open the investigation</a>
      </p>
    );
  }
  if (item === null) return <>{absent}</>;
  const { investigateHeld, dismissHeld } = actions;
  const { detail } = item;
  const answer = async (act: "investigate" | "dismiss") => {
    if (act === "investigate" && detail.resolved && confirming !== true) {
      setError(null);
      setConfirming(true);
      return;
    }
    setBusy(act);
    setError(null);
    try {
      if (act === "investigate" && investigateHeld !== undefined) {
        const started = await investigateHeld(item.id);
        setRun(started.run_id);
        onAnswered(
          started.joined
            ? "An investigation already open on this alarm took it."
            : "Investigation started, past today's limit.",
        );
      } else if (act === "dismiss" && dismissHeld !== undefined) {
        await dismissHeld(item.id);
        onAnswered("Dismissed. Nothing was started for this alarm.");
      }
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      {detail.resolved ? (
        <p>
          This alarm has resolved since it was held. It would have started an investigation, but today's limit on
          automatic investigations had been reached. You can still start one, or dismiss it.
        </p>
      ) : (
        <p>
          This alarm would have started an investigation, but today's limit on automatic investigations had been
          reached. Start it now, or dismiss it.
        </p>
      )}
      <dl className="kv">
        <dt>Severity</dt>
        <dd>{severityLabel(detail.severity)}</dd>
        <dt>Source</dt>
        <dd>{detail.source ?? "An alert source"}</dd>
        {detail.account_id || detail.region ? (
          <>
            <dt>Where</dt>
            <dd>{[detail.account_id, detail.region].filter(Boolean).join(" · ")}</dd>
          </>
        ) : null}
        <dt>Fired</dt>
        <dd>
          {detail.occurrences === 1 ? "Once" : `${detail.occurrences} times`}, last {utcTime(detail.last_seen_at)}
        </dd>
      </dl>
      {confirming === true ? (
        <Notice>
          <span>This alarm has resolved. Start an investigation anyway?</span>
        </Notice>
      ) : null}
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {confirming === true ? (
        // Its own key, and Back in the first place: a second press of a double-click lands
        // on a new node that is never an act, so it cannot answer the question it raised.
        <div className="actions" key="confirm">
          <button type="button" className="btn" disabled={busy !== null} onClick={() => setConfirming(false)}>
            Back
          </button>
          <button
            ref={yesRef}
            type="button"
            className="btn btn--primary"
            disabled={busy !== null}
            onClick={() => void answer("investigate")}
          >
            {busy === "investigate" ? "Starting…" : "Yes, investigate it"}
          </button>
        </div>
      ) : (
        <div className="actions" key="choose">
          <button
            ref={investigateRef}
            type="button"
            className="btn btn--primary"
            disabled={busy !== null || investigateHeld === undefined}
            onClick={() => void answer("investigate")}
          >
            {busy === "investigate" ? "Starting…" : "Investigate now"}
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy !== null || dismissHeld === undefined}
            onClick={() => void answer("dismiss")}
          >
            {busy === "dismiss" ? "Dismissing…" : "Dismiss"}
          </button>
        </div>
      )}
    </>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab from the last control goes to the first, and Shift+Tab from the first to the last. */
function keepFocusInside(event: KeyboardEvent<HTMLDialogElement>): void {
  if (event.key !== "Tab") return;
  const inside = [...event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.getClientRects().length > 0,
  );
  if (inside.length === 0) return;
  const first = inside[0]!;
  const last = inside[inside.length - 1]!;
  const at = document.activeElement;
  if (event.shiftKey && (at === first || !event.currentTarget.contains(at))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (at === last || !event.currentTarget.contains(at))) {
    event.preventDefault();
    first.focus();
  }
}

/** Each kind's answer, as its old screen gave it. A kind without one does not type-check. */
const BODIES: { readonly [K in InboxKind]: ComponentType<BodyProps<K>> } = {
  change_approval: ChangeBody,
  consent_request: ConsentBody,
  capability_request: CapabilityBody,
  invitation: InvitationBody,
  held_alert: HeldAlertBody,
};

/** The membership that selects a context, if the person holds one. */
function membershipFor(memberships: Membership[] | null, choice: ContextChoice): Membership | null {
  return (
    memberships?.find((m) =>
      "workspace_id" in choice ? m.workspace_id === choice.workspace_id : m.kind === "org" && m.org_id === choice.org_id,
    ) ?? null
  );
}

export function InboxDrawer({
  open,
  item,
  answered = null,
  filter,
  session,
  contextError,
  linkArrived = null,
  memberships = null,
  sources,
  actions,
  address,
  onClose,
}: {
  open: OpenItem;
  /** The inbox's row for it, or null before the inbox is read or once it has gone. */
  item: InboxItem | null;
  /** The inbox's answered row for it, when it was answered before the drawer opened (#858). */
  answered?: AnsweredItem | null;
  /** The filter the list is under, kept by the retry's location. */
  filter: string | null;
  session: SessionResponse;
  /** The console's refusal of the last context selection, if there was one. */
  contextError: string | null;
  /** The context this drawer's location selected, once there (#858). */
  linkArrived?: ContextChoice | null;
  /** The contexts the person holds, or null while unread. */
  memberships?: Membership[] | null;
  sources: DrawerSources;
  actions: DrawerActions;
  address: string;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  // The row as last seen: once answered, the item leaves the inbox's waiting list, and the
  // drawer keeps its name rather than falling back to the kind's.
  const [seen, setSeen] = useState<InboxItem | null>(item);
  if (item !== null && item !== seen) setSeen(item);
  const shown: RowItem | null = item ?? seen ?? answered;
  const spec = INBOX_KINDS[open.kind];
  // The context's own name where the inbox does not list the item: a mailed link (#858).
  const held = open.context === null ? null : membershipFor(memberships, open.context);
  const workspaceName = shown?.workspace_name ?? held?.name ?? null;
  const title = shown === null ? spec.one : rowOf(shown).subject(shown);
  const key = openKey(open);

  useEffect(() => {
    const element = dialog.current;
    if (element !== null && !element.open) element.showModal?.();
    return () => {
      element?.close?.();
      // Back to the row that opened it: the list is still there behind the drawer.
      document.querySelector<HTMLElement>(`[data-inbox-open="${key}"]`)?.focus();
    };
  }, [key]);

  const here = open.context === null || inLinkContext(session, open.context);
  // The item's link moved the whole console here, and it stays once the drawer closes (#858).
  const landed = linkArrived === null ? null : (held?.name ?? workspaceName);
  const elsewhere = workspaceName ?? "its workspace";
  // Written after the status is on the page, never with it (#971): the console's move
  // remounts the drawer, and a live region inserted already holding its text is not
  // announced by every screen reader.
  const [arrival, setArrival] = useState<string | null>(null);
  useEffect(() => setArrival(landed === null ? null : `You're now in ${landed}.`), [landed]);
  const Body = BODIES[open.kind] as ComponentType<BodyProps<InboxKind>>;

  let body: ReactNode;
  if (!here) {
    const place = open.context !== null && "org_id" in open.context ? "organisation" : "workspace";
    body =
      contextError === null ? (
        <p role="status">Selecting {elsewhere} to answer in…</p>
      ) : memberships !== null && held === null ? (
        // Not theirs: loading the same address would be refused the same way, so no link.
        <Notice tone="bad">You're not a member of that {place}, so you can't answer this here.</Notice>
      ) : (
        <>
          <Notice tone="bad">
            {elsewhere === "its workspace" ? "The workspace" : elsewhere} could not be selected, so this cannot be
            answered here. {contextError}
          </Notice>
          <p>
            <a className="btn" href={inboxPath({ filter, open })}>
              Open in {elsewhere}
            </a>
          </p>
        </>
      );
  } else {
    body = (
      <Body
        id={open.id}
        item={item}
        sources={sources}
        actions={actions}
        address={address}
        answered={receipt !== null}
        onAnswered={(text) => setReceipt(text)}
        onClose={onClose}
        absent={
          // Answered or expired: nothing to answer here or anywhere, so no link (#858).
          <Notice>
            {answered === null
              ? `This is no longer waiting for your answer in ${elsewhere}: it was answered, or it expired.`
              : `This was already answered: ${answerSentence(answered)}.`}
          </Notice>
        }
      />
    );
  }

  return (
    <dialog
      ref={dialog}
      className="drawer"
      aria-labelledby="inbox-drawer-title"
      data-testid="inbox-drawer"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onKeyDown={keepFocusInside}
      onClick={(event) => {
        if (event.target === dialog.current) onClose();
      }}
    >
      <div className="drawer__head">
        <div>
          <p className="eyebrow">
            {spec.one}
            {workspaceName === null ? null : <> · {workspaceName}</>}
          </p>
          <h2 id="inbox-drawer-title">{title}</h2>
        </div>
        <button type="button" className="btn btn--quiet" onClick={onClose}>
          Close
        </button>
      </div>
      <p role="status" className="drawer__arrival">
        {arrival}
      </p>
      <div className="drawer__body stack">
        {receipt === null ? null : <Notice>{receipt}</Notice>}
        {body}
      </div>
    </dialog>
  );
}
